<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Exit Pipeline</h5>
        <p class="page-sub">Active cases grouped by stage</p>
      </div>
      <div class="header-actions">
        <button class="btn-outline" @click="$router.push($url('/hr/exits'))">
          <font-awesome-icon icon="list" /> All Cases
        </button>
        <button class="btn-primary-action" @click="$router.push($url('/hr/exits/create'))">
          <font-awesome-icon icon="plus" /> Initiate Exit
        </button>
      </div>
    </div>

    <div v-if="loading" class="pipeline-skeleton">
      <div v-for="n in 4" :key="n" class="sk-col">
        <div class="sk-col-header"></div>
        <div v-for="i in 2" :key="i" class="sk-card"></div>
      </div>
    </div>

    <div v-else class="pipeline-board">
      <div v-for="stage in stages" :key="stage.key" class="pipeline-col">
        <div class="col-header" :style="{ borderTopColor: stage.color }">
          <span class="col-title">{{ stage.label }}</span>
          <span class="col-badge" :style="{ background: stage.color }">{{ (grouped[stage.key] || []).length }}</span>
        </div>
        <div class="col-cards">
          <div
            v-for="c in (grouped[stage.key] || [])"
            :key="c.id"
            class="case-card"
            @click="$router.push($url('/hr/exits/' + c.id))"
          >
            <div class="card-header-row">
              <span class="case-no">{{ c.case_number }}</span>
              <span class="type-badge" :class="'type--' + c.type">{{ typeLabel(c.type) }}</span>
            </div>
            <div class="emp-name">{{ empName(c.employee) }}</div>
            <div v-if="c.employee?.department?.name" class="emp-dept">{{ c.employee.department.name }}</div>
            <div v-if="c.last_working_day" class="lwd-row">
              <font-awesome-icon icon="calendar-day" class="lwd-icon" />
              LWD: {{ formatDate(c.last_working_day) }}
            </div>
          </div>
          <div v-if="!(grouped[stage.key] || []).length" class="empty-col">No cases</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'ExitPipeline',
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true,
      grouped: {},
      stages: [
        { key: 'pending_approval', label: 'Pending Approval', color: '#f59e0b' },
        { key: 'approved',         label: 'Approved',          color: '#3b82f6' },
        { key: 'in_progress',      label: 'In Progress',       color: '#8b5cf6' },
        { key: 'clearance',        label: 'Clearance',         color: '#ec4899' },
        { key: 'fnf_pending',      label: 'F&F Pending',       color: '#ef4444' },
      ],
    }
  },
  created() { this.load() },
  methods: {
    async load() {
      this.loading = true
      try {
        const r = await axios.get('exits/pipeline')
        this.grouped = r.data.data || {}
      } catch { this.toast.error('Failed to load pipeline.') }
      finally { this.loading = false }
    },
    empName(emp) {
      if (!emp) return '—'
      return `${emp.first_name} ${emp.last_name}`
    },
    typeLabel(t) {
      const map = { resignation: 'Resign', termination: 'Terminate', retirement: 'Retire', redundancy: 'Redundancy', death: 'Death' }
      return map[t] || t
    },
    formatDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.header-actions { display:flex; gap:10px; flex-wrap:wrap; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover { background:#003b8e; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#004AAD; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1.5px solid #004AAD; cursor:pointer; font-family:inherit; }
.btn-outline:hover { background:#f0f4ff; }

.pipeline-board { display:flex; gap:16px; overflow-x:auto; padding-bottom:20px; }
.pipeline-col   { flex:0 0 240px; min-width:240px; }

.col-header { display:flex; align-items:center; justify-content:space-between; background:#fff; border:1px solid #e4eaf2; border-top:3px solid; border-radius:10px 10px 0 0; padding:10px 14px; }
.col-title  { font-size:12px; font-weight:700; color:#1a2b45; text-transform:uppercase; letter-spacing:.04em; }
.col-badge  { color:#fff; font-size:11px; font-weight:700; padding:2px 8px; border-radius:10px; }

.col-cards  { display:flex; flex-direction:column; gap:10px; padding:10px 0; }

.case-card  { background:#fff; border:1px solid #e4eaf2; border-radius:10px; padding:14px; cursor:pointer; transition:box-shadow .15s,transform .15s; }
.case-card:hover { box-shadow:0 4px 16px rgba(0,74,173,.1); transform:translateY(-1px); }

.card-header-row { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; }
.case-no   { font-size:11px; font-weight:700; color:#004AAD; }
.emp-name  { font-size:13px; font-weight:600; color:#1a2b45; margin-bottom:2px; }
.emp-dept  { font-size:11px; color:#7a90b0; margin-bottom:6px; }
.lwd-row   { display:flex; align-items:center; gap:5px; font-size:11px; color:#4a5e78; }
.lwd-icon  { color:#ef4444; font-size:10px; }

.type-badge { font-size:10px; font-weight:600; padding:2px 7px; border-radius:6px; }
.type--resignation  { background:#fef3c7; color:#92400e; }
.type--termination  { background:#fee2e2; color:#991b1b; }
.type--retirement   { background:#e0f2fe; color:#0369a1; }
.type--redundancy   { background:#f3e8ff; color:#6b21a8; }
.type--death        { background:#f1f5f9; color:#475569; }

.empty-col { font-size:12px; color:#aab8cc; text-align:center; padding:20px 0; }

.pipeline-skeleton { display:flex; gap:16px; }
.sk-col { flex:0 0 240px; }
.sk-col-header { height:44px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:10px 10px 0 0; margin-bottom:10px; }
.sk-card { height:90px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:10px; margin-bottom:10px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
</style>
